import { Component, OnInit, OnDestroy, Inject, ViewChild } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { LoadingType, LoadingColor } from '@constants/loading/1.0';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { pairwise, Subject, filter } from 'rxjs';
import { MessageType } from '@constants/message/1.0';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { TypeSelection } from '@components/type-selector/1.0';
import { OutOfStockUpdateInput } from '@generated/tcea/models/out-of-stock-update-input';
import { ThemePalette } from '@angular/material/core';
import { DateService } from '@services/utilities/1.0/date.service';
import { BrandService } from '@services/brand/1.0/brand.service';
import { StoreView } from '@interfaces/store/1.0';
import { startWith, takeUntil } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';
import { Tenant } from '@generated/tcea/models/tenant';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { StoreService } from '@services/store/1.0/store.service';
import { HttpHeaders } from '@angular/common/http';
import { Store } from '@interfaces/store/1.0';
import { NgIf } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MessageComponent } from '../../message/1.0/message.component';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import {
  MatFormField,
  MatLabel,
  MatSuffix,
  MatHint,
  MatError,
  MatPrefix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TypeSelectorComponent } from '../../type-selector/1.0/type-selector.component';
import { NgxMatDatetimePickerModule } from '@nng-components/angular-material-datetime-picker';
import { MatDatepickerToggle } from '@angular/material/datepicker';
import { MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { BadgeComponent } from '../../badge/1.0/badge.component';
import { LoadingComponent } from '../../loading/1.0/loading.component';
import { ButtonComponent } from '../../button/1.0/button.component';
import { Router } from '@angular/router';

const DATE_HM_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

@Component({
  selector: 'txp-select-bulk-stores-dialog',
  templateUrl: './select-bulk-stores-dialog.component.html',
  styleUrl: './select-bulk-stores-dialog.component.scss',
  standalone: true,
  imports: [
    MatDialogTitle,
    NgIf,
    CdkScrollable,
    MatDialogContent,
    MessageComponent,
    FormsModule,
    ReactiveFormsModule,
    MatRadioGroup,
    MatRadioButton,
    MatFormField,
    MatLabel,
    MatInput,
    TypeSelectorComponent,
    NgxMatDatetimePickerModule,
    MatDatepickerToggle,
    MatSuffix,
    MatIconButton,
    MatHint,
    MatError,
    MatPrefix,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCheckbox,
    MatCellDef,
    MatCell,
    MatSortHeader,
    BadgeComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    LoadingComponent,
    MatDialogActions,
    ButtonComponent,
    TranslateModule,
  ],
})
export class SelectBulkStoresDialogComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) sort: MatSort;
  brand: Tenant;
  stores: StoreView[] = [];
  selectedStores: StoreView[] = [];
  catalogEntryIds: string[] = [];
  storeId?: string; // Store ID when opened from store level
  storeDataSource: MatTableDataSource<StoreView> = new MatTableDataSource<StoreView>();
  storeDisplayColumns: string[] = [
    'select',
    'name',
    'storeNumber',
    'storeStatus',
    'storeAddress',
    'city',
  ];

  get storeFilterControl(): UntypedFormControl {
    return this.form.get('storeFilter') as UntypedFormControl;
  }
  storeSelection = new SelectionModel<StoreView>(true, []);

  public color: ThemePalette = 'primary';
  form: UntypedFormGroup;
  loadingType = LoadingType;
  loadingColor = LoadingColor;
  messageType = MessageType;
  buttonSize = ButtonSize;
  buttonStyle = ButtonStyle;
  defaultSchedule: any;
  dateTypeSelection: TypeSelection[];
  defaultFormValue: any;
  minDate: string = null;
  selectedDateType: string = null;
  setEndDate = false;
  isLoading = false;
  isUpdating = false;
  showError = false;
  showSuccess = false;
  successMessage = '';
  errorMessage = '';

  private unsubscribe = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    private dateService: DateService,
    private brandService: BrandService,
    private storeService: StoreService,
    private router: Router,
    private dialogRef: MatDialogRef<SelectBulkStoresDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      catalogEntryIds: string[];
      catalogEntryNames: string[];
      brandId: string;
      storeId?: string;
    },
  ) {
    this.dateTypeSelection = [
      {
        data: 'end',
        label: translateService.instant('store.taxes.itemAvailability.outOfStock.until.endOfDay'),
      },
      {
        data: 'date',
        label: translateService.instant(
          'store.taxes.itemAvailability.outOfStock.until.dateAndTime',
        ),
      },
    ];
    this.selectedDateType = this.dateTypeSelection[0].data;
    this.defaultSchedule = { until: null };
    this.defaultFormValue = {
      action: 'OUT',
      reason: '',
      type: 'RE_ENABLED',
      scheduler: this.defaultSchedule,
    };

    this.form = formBuilder.group({
      action: new UntypedFormControl('OUT'),
      reason: new UntypedFormControl(''),
      type: new UntypedFormControl('RE_ENABLED'),
      scheduler: formBuilder.group({
        until: new UntypedFormControl(null, [this.dateExistsValidator.bind(this)]),
      }),
      storeFilter: new UntypedFormControl(''),
    });
  }

  ngOnInit() {
    this.catalogEntryIds = this.data.catalogEntryIds || [];
    // Only honor storeId when opened under /store/:id (ignore sticky storeId from brand catalog)
    this.storeId = this.isInStoreRouteContext() ? this.data.storeId : undefined;
    this.brandService.currentBrandBehaviorSubject
      .pipe(
        filter((brand) => brand.isSet),
        takeUntil(this.unsubscribe),
      )
      .subscribe((brand) => {
        this.brand = brand;
        // Only load stores if not in store-level context
        if (!this.storeId) {
          this.loadStores();
        }
      });

    this.minDate = this.dateService.formatInTimeZone(new Date(), 'UTC', DATE_HM_FORMAT);

    this.form.valueChanges
      .pipe(startWith(this.form.value), pairwise(), takeUntil(this.unsubscribe))
      .subscribe(([prevData, currentData]) => {
        if (prevData.outOfStock && !currentData.outOfStock) {
          this.form.setValue(this.defaultFormValue, { emitEvent: false });
        }
      });

    this.form
      .get('storeFilter')
      .valueChanges.pipe(takeUntil(this.unsubscribe))
      .subscribe((value) => this.applyStoreFilter(value));
  }

  /**
   * Store-level catalog search lives under /store/:id/...
   * Brand catalog (/catalog/search) must show the multi-store picker.
   */
  private isInStoreRouteContext(): boolean {
    const path = (this.router.url || '').split('?')[0];
    return /^\/store\/[^/]+/.test(path);
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  loadStores() {
    if (!this.data.brandId) return;
    this.isLoading = true;
    this.storeService.get(this.data.brandId).subscribe({
      next: (stores) => {
        this.stores = stores;
        this.storeDataSource.data = stores;
        this.storeDataSource.sort = this.sort;
        // Reset selection when (re)loading the store list to avoid stale state
        this.selectedStores = [];
        this.storeDataSource.filterPredicate = (row: StoreView, filter: string) => {
          const value = (filter || '').toLowerCase();
          const fields = [
            row.name,
            row.storeNumber,
            row.storeStatus,
            (row as any).storeAddress,
            (row as any).city,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return fields.includes(value);
        };
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.showError = true;
        this.errorMessage = this.translateService.instant('store.loadError');
      },
    });
  }

  onStoreSelectionChange(store: StoreView, isSelected: boolean) {
    this.selectedStores = isSelected
      ? [...this.selectedStores, store]
      : this.selectedStores.filter((s) => s.id !== store.id);
  }
  isStoreSelected(store: StoreView): boolean {
    return this.selectedStores.some((s) => s.id === store.id);
  }
  applyStoreFilter(value: string) {
    this.storeDataSource.filter = (value || '').trim().toLowerCase();
  }
  private getRenderableStores(): StoreView[] {
    const anyDs: any = this.storeDataSource as any;
    return (anyDs.filteredData as StoreView[]) || this.storeDataSource.data || [];
  }
  hasSelection(): boolean {
    // If storeId is provided, we always have a selection
    if (this.storeId) {
      return true;
    }
    return this.selectedStores.length > 0;
  }
  isAllSelected() {
    const data = this.getRenderableStores();
    const numRows = data.length;
    if (!numRows) return false;
    const selectedIds = new Set(this.selectedStores.map((s) => s.id));
    return data.every((row) => selectedIds.has(row.id));
  }
  masterToggle() {
    const data = this.getRenderableStores();
    if (!data.length) {
      this.selectedStores = [];
      return;
    }
    if (this.isAllSelected()) {
      const ids = new Set(data.map((d) => d.id));
      this.selectedStores = this.selectedStores.filter((s) => !ids.has(s.id));
    } else {
      const existingById = new Map(
        this.selectedStores.map((s) => [s.id, s] as [string, StoreView]),
      );
      for (const row of data) {
        existingById.set(row.id, row);
      }
      this.selectedStores = Array.from(existingById.values());
    }
  }
  setCatalogEntryIds(ids: string[]) {
    this.catalogEntryIds = ids;
  }

  onSelectDateType(type) {
    this.selectedDateType = type;
    this.setEndDate = type === 'end';
    if (type === 'date' && !this.form.get('scheduler.until').value) {
      this.form.get('scheduler.until').setValue(this.minDate);
    }
    this.form.get('scheduler').updateValueAndValidity();
  }

  handleBulkOutOfStock() {
    // Validation: if storeId is provided, we don't need selectedStores
    if (!this.form.valid || this.catalogEntryIds.length === 0) {
      return;
    }
    if (!this.storeId && this.selectedStores.length === 0) {
      return;
    }

    const { action, reason, type } = this.form.value;
    const isOutOfStock = action === 'OUT';
    const hasSchedule = isOutOfStock && type === 'SPECIFIED_DATE';

    const headers = new HttpHeaders({ 'active-tenant-identifier': this.data.brandId });

    // If storeId is provided, use it directly; otherwise use selected stores
    const storesToProcess = this.storeId
      ? [{ id: this.storeId } as StoreView]
      : this.selectedStores;

    const detailsRequests = storesToProcess.map((s) => this.storeService.getOne(s.id, headers));

    this.isUpdating = true;
    this.showError = false;
    this.showSuccess = false;

    forkJoin(detailsRequests).subscribe({
      next: (details: Store[]) => {
        const storeIdToDetails: Record<string, Store> = {};
        details.forEach((d) => (storeIdToDetails[d.id] = d));
        const bulkUpdates: { storeId: string; updates: OutOfStockUpdateInput[] }[] = [];
        for (const store of storesToProcess) {
          const storeUpdates: OutOfStockUpdateInput[] = [];
          for (const catalogEntryId of this.catalogEntryIds) {
            const data: OutOfStockUpdateInput = {
              outOfStock: isOutOfStock,
              catalogEntryId,
              reason: '',
              source: 'TXA',
            };
            if (isOutOfStock) {
              const { scheduler } = this.form.value;
              const now = new Date();
              const tzCode = storeIdToDetails[store.id]?.timeZone?.timeZoneCode;
              const endOfDay = this.dateService.convertToTimeZone(
                this.dateService.endOf(now, 'day'),
                tzCode,
              );
              const untilEndOfDay = this.selectedDateType === 'end';
              let until: number;
              if (untilEndOfDay) {
                const storeTimezoneOffset = this.getStoreTimezoneOffset(tzCode);
                const endOfDayString = this.dateService.formatLocalDateTime(
                  endOfDay,
                  DATE_HM_FORMAT,
                );
                const dateWithTimezone =
                  endOfDayString + this.formatTimezoneOffset(storeTimezoneOffset);
                const utcDate = new Date(dateWithTimezone);
                until = Math.floor(utcDate.getTime() / 1000);
              } else {
                const formDateString = scheduler.until;
                const storeTimezoneOffset = this.getStoreTimezoneOffset(tzCode);
                const dateWithTimezone =
                  formDateString + this.formatTimezoneOffset(storeTimezoneOffset);
                const utcDate = new Date(dateWithTimezone);
                until = Math.floor(utcDate.getTime() / 1000);
              }
              const newScheduler = hasSchedule ? { until } : undefined;
              Object.assign(data, { reason, scheduler: newScheduler });
            }
            storeUpdates.push(data);
          }
          bulkUpdates.push({ storeId: store.id, updates: storeUpdates });
        }
        const updateRequests: Observable<void>[] = bulkUpdates.map(({ storeId, updates }) =>
          this.storeService.setOutOfStockBulk(this.data.brandId, storeId, updates),
        );
        forkJoin(updateRequests).subscribe({
          next: () => {
            this.isUpdating = false;
            this.showSuccess = true;
            this.successMessage = this.translateService.instant(
              'catalog.bulkOutOfStock.successMessage',
            );
          },
          error: () => {
            this.isUpdating = false;
            this.showError = true;
            this.errorMessage = this.translateService.instant(
              'catalog.bulkOutOfStock.errorMessage',
            );
          },
        });
      },
      error: () => {
        this.showError = true;
        this.errorMessage = this.translateService.instant('catalog.bulkOutOfStock.errorMessage');
      },
    });
  }

  handleCancel() {
    this.dialogRef.close();
  }

  dateValidator(value: number): ValidationErrors | undefined {
    if (!value) return null;
    const now = new Date();
    const after15Minutes = new Date(now.getTime() + 15 * 60 * 1000);
    const dateToCheck = new Date(value);
    return dateToCheck.getTime() >= after15Minutes.getTime() ? null : { invalid: true };
  }

  dateExistsValidator(formControl: AbstractControl): ValidationErrors | undefined {
    if (!formControl.parent) {
      return null;
    }
    if (this.form.get('type').value === 'SPECIFIED_DATE') {
      return this.dateValidator(new Date(formControl.value).getTime());
    }
    return null;
  }

  private getStoreTimezoneOffset(timeZoneCode?: string): number {
    if (!timeZoneCode) return 0;
    const referenceDate = new Date();
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timeZoneCode,
      timeZoneName: 'longOffset',
    });
    const parts = formatter.formatToParts(referenceDate);
    const offsetString = parts.find((part) => part.type === 'timeZoneName')?.value;
    if (!offsetString) return 0;
    const match = offsetString.match(/GMT([+-])(\d{2}):(\d{2})/);
    if (!match) return 0;
    const sign = match[1] === '+' ? 1 : -1;
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);
    return sign * (hours * 60 + minutes);
  }

  private formatTimezoneOffset(offset: number): string {
    const absOffset = Math.abs(offset);
    const hours = Math.floor(absOffset / 60);
    const minutes = absOffset % 60;
    return `${offset < 0 ? '-' : '+'}${hours.toString().padStart(2, '0')}${minutes.toString().padStart(2, '0')}`;
  }
}
